import { Input, InputRef, Modal } from "antd";
import LoadingBig from '../../../../assets/images/lottie/loading_big.json';
import { useCreateNickNameWeb } from "../../model";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { isIOS } from "react-device-detect";
import "./index.css"
import { CircleCloseIcon } from "../../../../shared/icon";
import Lottie from "lottie-react";
import { useExistByNickName } from "./model";
import { androidOnInputFocus } from "../../../../utils";
import { useNavigate } from "react-router-dom";

interface IProps {
    isNickNameEmpty?: boolean;
    isOpen: boolean;
    setIsOpen: Function;
}

const NickNameBottomSheet = ({ isNickNameEmpty, isOpen, setIsOpen }: IProps) => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [isNickNameValid, setIsNickNameValid] = useState<boolean>(false);
    const [contentHeight, setContentHeight] = useState<number>(0);
    const minWidth = window.innerWidth <= 480 ? '100vw' : '480px';
    const marginInline = window.innerWidth <= 480 ? '0' : (window.innerWidth - 480) / 2;
    const [nickName, setNickName] = useState<string>('');
    const [showCheckContent, setShowCheckContent] = useState<React.ReactNode>(<div style={{ height: '18px' }}></div>);
    const [isInputStop, setIsInputStop] = useState<boolean>(false)
    const inputStopTimerRef = useRef<NodeJS.Timeout | null>(null);
    const inputRef = useRef<InputRef>(null);
    const modalPannerRef = useRef<HTMLDivElement>(null);
    const [isInputLoading, setIsInputLoading] = useState<boolean>(false)
    const createNickNameWeb = useCreateNickNameWeb()
    const [inputFocus, setInputFocus] = useState<boolean>(false)

    const getValidationMessage = (inputValue: string): JSX.Element | null | undefined => {
        if (inputValue.length === 0) return <div style={{ height: '18px' }}></div>;
        if (/\s/.test(inputValue)) return <div className="body/b1 error/e700">닉네임은 띄어쓰기가 불가합니다.</div>;
        if (/[^a-zA-Z0-9가-힣]/.test(inputValue)) return <div className="body/b1 error/e700">특수문자 및 기호가 포함된 닉네임은 불가합니다.</div>;
        if (inputValue.length < 3) return <div className="body/b1 error/e700">3글자 이상 입력해주세요.</div>;
        return null;
    };

    const { data: isExistNickName, isFetching, refetch } = useExistByNickName(nickName, getValidationMessage(nickName) === null && isNickNameValid);

    const onClickNickNameCreateButton = () => {
        if (!isNickNameValid || createNickNameWeb.isPending) return;
        createNickNameWeb.mutate(nickName,
            {
                onSuccess: (res) => {
                    if (res.data) {
                        setIsOpen(false);
                        queryClient.invalidateQueries({ queryKey: ['nickName'] })
                    }
                }
            })
    }

    const isInputStopTime = () => {
        if (inputStopTimerRef.current) clearTimeout(inputStopTimerRef.current);
        setIsInputStop(false)
        inputStopTimerRef.current = setTimeout(() => {
            setIsInputStop(true)
        }, 300)
    }

    const nicknameInputOnFocusAndBlur = (isFocus: boolean) => {
        setInputFocus(isFocus)
        androidOnInputFocus(isFocus)
    }

    useLayoutEffect(() => {
        if (!isOpen) return
        setContentHeight(219 + 26 + (isIOS && !inputFocus ? 34 : 0))
    }, [isOpen, showCheckContent, isNickNameValid, inputFocus])

    useEffect(() => {
        if (!isInputStop) return;
        setIsInputLoading(false);
        getValidationMessage(nickName) === null && refetch();
    }, [isInputStop])

    useEffect(() => {
        const validationMessage = getValidationMessage(nickName);

        if (validationMessage) {
            setIsNickNameValid(false);
            setShowCheckContent(validationMessage);
        } else {
            setShowCheckContent(<div style={{ height: '18px' }}></div>);
        }
    }, [nickName]);

    useEffect(() => {
        if (isFetching) return;
        setIsInputLoading(false)

        const validationMessage = getValidationMessage(nickName);
        if (validationMessage || validationMessage === undefined) return;
        if (isExistNickName === undefined) {
            setIsNickNameValid(false);
            setShowCheckContent(<div className="body/b1 error/e700">네트워크에 문제가 있습니다.</div>);
            return
        }
        const isAvailable = !isExistNickName;
        const resultContent = isAvailable
            ? <div className="body/b1 success/sc600">사용 가능한 닉네임 입니다.</div>
            : <div className="body/b1 error/e700">이미 사용중인 닉네임입니다.</div>;

        setIsNickNameValid(isAvailable);
        setShowCheckContent(resultContent);
    }, [isFetching]);

    useEffect(() => {
        if (isNickNameEmpty === false) return;
        if (!isOpen) { inputStopTimerRef.current && clearTimeout(inputStopTimerRef.current); return }
        navigate('#', { replace: true })
        inputRef.current?.focus();
    }, [isOpen, isNickNameEmpty])

    useEffect(() => {
        return () => { androidOnInputFocus(false) }
    }, [])

    useEffect(() => {
        if (!inputFocus || !window.visualViewport) return;
        const handleVisualViewportResize = () => {
            setTimeout(() => {
                modalPannerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
            }, 300)
        };
        window.visualViewport.onresize = handleVisualViewportResize;

        return () => {
            window.visualViewport?.removeEventListener('resize', handleVisualViewportResize);
        };

    }, [inputFocus])

    return <>
        <Modal transitionName="custom-modal-nickname"
            panelRef={modalPannerRef}
            style={{ position: 'fixed', transition: 'top 0.3s ease', top: !isOpen ? '100vh' : `calc(100vh - ${contentHeight}px)`, padding: 0, margin: 0 }}
            styles={{ content: { minWidth: minWidth, height: `${contentHeight}px`, overflow: "scroll", borderRadius: "var(--space16) var(--space16) 0 0", padding: `var(--space20)`, marginInline: marginInline + 'px' }, body: { paddingBottom: isIOS ? 34 : 0 } }}
            open={isOpen} closeIcon={null} footer={null} >
            <div style={{ marginBottom: 'var(--space20)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div className="title/t1 base/black">
                    닉네임 설정
                </div>
            </div>

            <div className="body/b2 secondary/s800" style={{ marginBottom: 'var(--space8)' }}>커뮤니티 이용을 위해 닉네임 설정이 필요합니다.</div>
            <Input
                style={{ marginBottom: '8px' }}
                ref={inputRef}
                autoFocus
                onFocus={() => nicknameInputOnFocusAndBlur(true)}
                onBlur={() => nicknameInputOnFocusAndBlur(false)}
                allowClear={!(isInputLoading || isFetching) && { clearIcon: <CircleCloseIcon fill1="var(--basewhite)" fill2="var(--secondarys400)" stroke1="var(--secondarys400)" onClick={() => { setIsNickNameValid(false); setNickName('') }} /> }}
                className="custom-input-textarea custom-nickname-input"
                classNames={{ input: 'body/b2base/black-important' }}
                value={nickName}
                maxLength={15}
                onChange={(e) => { setIsInputLoading(true); isInputStopTime(); setNickName(e.target.value ?? '') }}
                placeholder="3글자 이상 입력"
                suffix={(isInputLoading || isFetching) && <Lottie style={{ width: 'var(--space16)', height: 'var(--space16)' }} animationData={LoadingBig} autoplay={true} />}
                onPressEnter={onClickNickNameCreateButton}
            />
            {showCheckContent}

            <div className={"sub-title/st3 " + (createNickNameWeb.isPending || !isNickNameValid ? 'secondary/s400' : "base/white")}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space16) var(--space20)', marginTop: 'var(--space20)', borderRadius: 'var(--radius4)', backgroundColor: !isNickNameValid || createNickNameWeb.isPending ? 'var(--secondarys200)' : 'var(--baseblack)' }}
                onClick={() => onClickNickNameCreateButton()}>
                설정하기
                {createNickNameWeb.isPending && <Lottie style={{ width: 16, height: 16, paddingBlock: '1.5px', marginLeft: '8px' }} animationData={LoadingBig} autoplay={true} />}
            </div>
        </Modal>
    </>
}

export { NickNameBottomSheet };


